import {AutocompleteControl, CheckboxControl, Helper} from 'appShared/classes';
import {ColorCodesEnum, ComponentTypesEnum} from 'appShared/enums/common';
import {BaseControl, RadioGroupControl} from 'appShared/classes/controls';

import {MessagingRequestTypesEnum} from '@spa/api';

import {InArrayValidator} from '@spa/validators';

import {
    AmsRecipientEmailSelector,
    AmsRecipientNameSelector,
    AmsRecipientSubjectIdSelector,
    AmsRecipientTenantIdSelector,
    AmsRecipientUserIdSelector,
    AmsRecipientUserRelationTypeSelector,
    AmsRecipientUserRelationTypeTxtSelector,
    MessageRequestBodySelector,
    MessageRequestChannelEmailSelector,
    MessageRequestChannelPullSelector,
    MessageRequestChannelPushSelector,
    MessageRequestImportanceLevelSelector,
    MessageRequestKeyCodeSelector,
    MessageRequestMessagingTemplateKeyCodeSelector,
    MessageRequestPushNotificationBodySelector,
    MessageRequestSubjectSelector,
    MessageRequestTypeSelector
} from 'appAssets/selectors';
import {ReportTableColumn, ReportTableConfigInterface, ReportTableData} from 'appShared/components';
import { Validators } from '@angular/forms';

export interface MessageRequestControlsInterface {
    messageType: RadioGroupControl;
    subject: BaseControl;
    body: BaseControl;
    keyCode: BaseControl;
    messageKeyCode: AutocompleteControl;
    importanceLevel: CheckboxControl;
    channelEmail: CheckboxControl;
    channelPush: CheckboxControl;
    channelPull: CheckboxControl;
    pushNotificationBody: BaseControl;
}

export const PushNotificationBodyRequestMaxLength = 256;

export const MessageRequestControls = (
    hasCreateWithTemplatePermission: boolean,
    hasCreateManualPermission: boolean
): MessageRequestControlsInterface => {
    return {
        messageType: new RadioGroupControl({
            name: MessageRequestTypeSelector,
            value: hasCreateWithTemplatePermission ? MessagingRequestTypesEnum.Template : MessagingRequestTypesEnum.Manual,
            placeholder: '',
            validators: [InArrayValidator([
                MessagingRequestTypesEnum.Manual,
                MessagingRequestTypesEnum.Template,
            ])],
            componentType: ComponentTypesEnum.RadioGroup,
            options: [
                {
                    label: Helper.textsHelper().getMessageRequestTypeName(MessagingRequestTypesEnum.Manual),
                    value: MessagingRequestTypesEnum.Manual,
                    color: ColorCodesEnum.Primary,
                    disabled: !hasCreateManualPermission
                },
                {
                    label: Helper.textsHelper().getMessageRequestTypeName(MessagingRequestTypesEnum.Template),
                    value: MessagingRequestTypesEnum.Template,
                    color: ColorCodesEnum.Primary,
                    disabled: !hasCreateWithTemplatePermission
                },
            ],
            sortIndex: 1,
            floatingLabelNever: true
        }),
        keyCode: new BaseControl({
            name: MessageRequestKeyCodeSelector,
            value: null,
            placeholder: 'Key Code',
            validators: [],
            componentType: ComponentTypesEnum.Textarea,
            sortIndex: 5,
            floatingLabelNever: true
        }),
        subject: new BaseControl({
            name: MessageRequestSubjectSelector,
            value: null,
            placeholder: 'Message Subject',
            validators: [],
            componentType: ComponentTypesEnum.Textarea,
            sortIndex: 5,
            floatingLabelNever: true
        }),
        body: new BaseControl({
            name: MessageRequestBodySelector,
            value: null,
            placeholder: 'Message Body',
            validators: [],
            componentType: ComponentTypesEnum.Textarea,
            sortIndex: 6,
            floatingLabelNever: true
        }),
        messageKeyCode: new AutocompleteControl({
            name: MessageRequestMessagingTemplateKeyCodeSelector,
            value: null,
            placeholder: 'Message Template',
            validators: [],
            componentType: ComponentTypesEnum.Autocomplete,
            options: [],
        }),
        importanceLevel: new CheckboxControl({
            name: MessageRequestImportanceLevelSelector,
            value: false,
            placeholder: 'Important',
            validators: [],
            componentType: ComponentTypesEnum.Checkbox,
            color: ColorCodesEnum.Primary,
            expectBooleanValue: true,
            hidden: true // If the selected distribution channel is PULL, this input will be visible.(PULL is not implemented yet)
        }),
        channelEmail: new CheckboxControl({
            name: MessageRequestChannelEmailSelector,
            value: false,
            placeholder: 'E-mail',
            validators: [],
            componentType: ComponentTypesEnum.Checkbox,
            color: ColorCodesEnum.Primary,
            expectBooleanValue: true,
            hidden: true,
        }),
        channelPush: new CheckboxControl({
            name: MessageRequestChannelPushSelector,
            value: false,
            placeholder: 'Push',
            validators: [],
            componentType: ComponentTypesEnum.Checkbox,
            color: ColorCodesEnum.Primary,
            expectBooleanValue: true,
            hidden: true,
        }),
        channelPull: new CheckboxControl({
            name: MessageRequestChannelPullSelector,
            value: false,
            placeholder: 'Pull',
            validators: [],
            componentType: ComponentTypesEnum.Checkbox,
            color: ColorCodesEnum.Primary,
            expectBooleanValue: true,
            hidden: true,
        }),
        pushNotificationBody: new BaseControl({
            name: MessageRequestPushNotificationBodySelector,
            value: null,
            placeholder: 'Push Notification Body',
            validators: [Validators.maxLength(PushNotificationBodyRequestMaxLength)],
            componentType: ComponentTypesEnum.Textarea,
            sortIndex: 7,
            floatingLabelNever: true
        }),
    };
};

export const AmsRecipientDataTableItems = (): ReportTableData[] => {
    return [
        new ReportTableData<number>({
            colId: AmsRecipientUserIdSelector,
            value: null,
            hidden: true
        }),
        new ReportTableData<number>({
            colId: AmsRecipientSubjectIdSelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: AmsRecipientUserRelationTypeSelector,
            value: null,
            hidden: true
        }),
        new ReportTableData<number>({
            colId: AmsRecipientUserRelationTypeTxtSelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: AmsRecipientNameSelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: AmsRecipientEmailSelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: AmsRecipientTenantIdSelector,
            value: null,
            hidden: true
        }),
    ];
};

export const AmsRecipientDataTableConfig = (hasToggleableOrCheckableRows: boolean): ReportTableConfigInterface => {
    const columns = [
        new ReportTableColumn({
            label: 'Customer Id',
            selector: AmsRecipientSubjectIdSelector
        }),
        new ReportTableColumn({
            label: 'User Type',
            selector: AmsRecipientUserRelationTypeSelector
        }),
        new ReportTableColumn({
            label: 'Name',
            selector: AmsRecipientNameSelector
        }),
        new ReportTableColumn({
            label: 'Email',
            selector: AmsRecipientEmailSelector
        }),
    ];

    return {
        rows: [],
        columns,
        hiddenColumnIds: [],
        actionMenu: false,
        disableColumnsOptions: true,
        sortByRowIndex: true,
        hasToggleableOrCheckableRows,
    };
};
